import { useAuth } from "@/contexts/AuthContext";
import Api from "@/services/Api";
import { SyncService } from "@/services/Sync";
import { MaterialCommunityIcons, MaterialIcons } from "@/ui/icons";
import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView,
  type BottomSheetBackdropProps,
} from "@/ui/bottom-sheet";
import { useRouter } from "@/navigation";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ActivityIndicator } from "@/ui/ActivityIndicator";
import { Alert } from "@/platform/alert";
import { DeviceEventEmitter } from "@/platform/events";
import { Image } from "@/ui/Image";
import { Modal } from "@/ui/Modal";
import { useWindowDimensions } from "@/platform/dimensions";
import { cx } from "@/ui/cx";
import iconPng from "@/assets/images/icon.png";
import { cacheEmpresaLogosFromList } from "./empresaLogo";
import s from "./EmpresaSwitcher.module.css";

const PRIMARY = "#294956";
const SECONDARY = "#516e7a";
const SUCCESS = "#34b863";

// WEB: asset importado vira URL string (aceita direto pelo `source` do <Image>).
const DEFAULT_EMPRESA_LOGO: string = iconPng;
const EMPRESA_LOGO_BASE_URL = "";
const EMPRESA_LOGO_QUERY = "";

/** Resolve a logo da empresa (base64, URL completa ou arquivo) com fallback local. */
const getEmpresaLogo = (logo?: string | null) => {
  const value = String(logo || "").trim();
  if (!value) return DEFAULT_EMPRESA_LOGO;
  if (value.startsWith("data:image")) return { uri: value };
  if (/^https?:\/\//i.test(value)) return { uri: value };
  const fileName = value.split("/").pop();
  if (fileName)
    return { uri: `${EMPRESA_LOGO_BASE_URL}${fileName}${EMPRESA_LOGO_QUERY}` };
  return DEFAULT_EMPRESA_LOGO;
};

type EmpresaItem = {
  id: string;
  nome: string;
  hash: string;
  logo?: string | null;
  cargo?: string | null;
  matricula?: string | null;
  ativo_inativo?: string | null;
  id_adm_colaborador?: number | null;
};

/**
 * Empresas de HOMOLOGAÇÃO (staging) não são listadas. Cobre as grafias
 * "HOMOLOGACAO" e "HOMOLOCACAO" (esta usada no hash/banco de homologação),
 * em qualquer parte do nome ou do hash.
 */
const ehHomologacao = (nome?: string | null, hash?: string | null): boolean => {
  const n = String(nome || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();
  const h = String(hash || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();
  // /HOMOLO[GC]/ cobre tanto "HOMOLOGACAO" quanto a grafia "HOMOLOCACAO".
  return /HOMOLO[GC]/.test(n) || /HOMOLO[GC]/.test(h);
};

const normalizeEmpresas = (payload: any): EmpresaItem[] => {
  const raw: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.empresas)
        ? payload.empresas
        : payload
          ? [payload]
          : [];

  const ordenadas = raw
    .map((item: any, i: number) => {
      const banco = String(item?.banco_dados ?? item?.database ?? "");
      const nome = String(
        item?.nome_fantasia ??
          item?.nome ??
          item?.razao_social ??
          item?.fantasia ??
          item?.nome_razao?.split?.(" ")?.[0] ??
          (banco
            ? banco.replace(/^bd_/i, "").replace(/_/g, " ").toUpperCase()
            : `Empresa ${i + 1}`),
      ).trim();
      return {
        // Chave única: o mesmo CPF pode ter 2 registros na mesma empresa (mesmo hash,
        // id_adm_colaborador diferentes — ex.: ativo + inativo). O índice garante unicidade.
        id: `${String(item?.hash ?? banco ?? "e")}-${item?.id_adm_colaborador ?? "x"}-${i}`,
        nome,
        hash: String(item?.hash ?? item?.Hash ?? ""),
        logo:
          item?.img_icon ?? item?.img_logo ?? item?.img_logo_horizontal ?? null,
        cargo: item?.cargo ?? null,
        matricula: item?.matricula ?? null,
        ativo_inativo: item?.ativo_inativo ?? null,
        id_adm_colaborador: item?.id_adm_colaborador ?? null,
      };
    })
    // Ordena por: hash → nome_fantasia (nome) → ativo_inativo
    .sort(
      (a, b) =>
        String(a.hash || "").localeCompare(String(b.hash || "")) ||
        String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR") ||
        String(a.ativo_inativo || "").localeCompare(
          String(b.ativo_inativo || ""),
        ),
    );

  // Deduplica pelo VÍNCULO DENTRO DA EMPRESA — MESMA regra do Onboarding.
  //
  // A chave é `empresa|id|matrícula`. O id+matrícula continua ali porque o mesmo CPF
  // com vínculo ATIVO e INATIVO na MESMA empresa são DUAS entradas distintas (colapsar
  // por hash puro fazia o switcher mostrar MENOS empresas que o Onboarding).
  //
  // ⚠️ Mas a EMPRESA vem primeiro: sem ela, o `id_adm_colaborador` — autoincremento POR
  // TENANT, que COLIDE entre bancos — somado à matrícula opcional (quase sempre vazia)
  // dava a MESMA chave para empresas diferentes, e o switcher escondia todas menos a
  // primeira: quem tinha duas empresas ficava sem opção de troca.
  const vistas = new Set<string>();
  return ordenadas.filter((e: EmpresaItem) => {
    const empresaKey =
      String(e.hash || "")
        .trim()
        .toUpperCase() || `nome:${String(e.nome || "").toUpperCase()}`;
    const key = `${empresaKey}|${String(e.id_adm_colaborador ?? "")}|${String(e.matricula ?? "")}`;
    if (vistas.has(key)) return false;
    vistas.add(key);
    return true;
  });
};

export type EmpresaSwitcherRef = { present: () => void };

/**
 * Seletor de empresa em BottomSheetModal: lista as empresas do colaborador e faz
 * a TROCA RÁPIDA (sem senha, via token atual) chamando Login/trocarEmpresa.
 * Offline → modal central avisando que a função precisa de internet.
 */
const EmpresaSwitcher = forwardRef<EmpresaSwitcherRef>((_props, ref) => {
  // WEB: o `insets.bottom` do conteúdo do sheet virou `var(--safe-bottom)` no CSS Module.
  const { height: windowHeight } = useWindowDimensions();
  const router = useRouter();
  const { userData, signIn, syncFirebaseToken, idColaborador } = useAuth();
  const sheetRef = useRef<BottomSheetModal>(null);

  const [empresas, setEmpresas] = useState<EmpresaItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [semConexao, setSemConexao] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [offlineVisible, setOfflineVisible] = useState(false);

  const cpf = String((userData as any)?.cpf ?? "").trim();
  const currentHash = String((userData as any)?.hash ?? "").trim();
  // Vínculo logado: desambigua qual entrada é a ATUAL quando há ativo+inativo na MESMA
  // empresa (mesmo hash/matrícula iguais) — nesse caso só o id_adm_colaborador difere.
  const currentIdColab = String(
    idColaborador ??
      (userData as any)?.idColaborador ??
      (userData as any)?.id_adm_colaborador ??
      "",
  ).trim();
  // ID ÚNICO do vínculo logado — MESMA tratativa do Onboarding, que compara a seleção por
  // `id` (e o `id` carrega o índice → NUNCA marca duas, mesmo que hash, matrícula e
  // id_adm_colaborador coincidam e só o cargo difira). Escolhe o vínculo exato (hash +
  // id_adm_colaborador) e, na falta, a primeira entrada da mesma empresa (por hash).
  const empresaAtualId =
    empresas.find(
      (e) =>
        e.hash === currentHash &&
        (currentIdColab
          ? String(e.id_adm_colaborador ?? "") === currentIdColab
          : true),
    )?.id ??
    empresas.find((e) => e.hash === currentHash)?.id ??
    null;

  const carregar = useCallback(async () => {
    if (!cpf) return;
    setLoadingList(true);
    setSemConexao(false);
    try {
      // Sem internet não há como listar/trocar empresa — avisa "sem acesso".
      const online = await SyncService.checkInternet();
      if (!online) {
        setSemConexao(true);
        setEmpresas([]);
        return;
      }
      const resp = await Api.autenticacao.listarEmpresas(cpf);
      if (resp?.success) {
        setEmpresas(normalizeEmpresas(resp.done));
        // Cacheia as logos para o header (PageFragment) exibir a empresa atual sem flash.
        void cacheEmpresaLogosFromList(resp.done);
      } else {
        setSemConexao(true);
        setEmpresas([]);
      }
    } catch {
      setSemConexao(true);
      setEmpresas([]);
    } finally {
      setLoadingList(false);
    }
  }, [cpf]);

  useImperativeHandle(
    ref,
    () => ({
      present: () => {
        sheetRef.current?.present();
        void carregar();
      },
    }),
    [carregar],
  );

  const trocar = useCallback(
    async (emp: EmpresaItem) => {
      if (switchingId) return;
      // Já é EXATAMENTE o vínculo atual (id único) → só fecha. Comparar por `id` (não por
      // hash) evita confundir um vínculo GÊMEO da mesma empresa com "já é a atual" — antes,
      // tocar no gêmeo caía aqui e não trocava (parecia selecionar a errada).
      if (emp.id === empresaAtualId) {
        sheetRef.current?.dismiss();
        return;
      }
      setSwitchingId(emp.id);
      // Estética: fecha o sheet e LIGA O SKELETON da Home imediatamente no clique,
      // ANTES de qualquer await (checagem de internet / API). Também acende o loader
      // no logo do header (PageFragment).
      sheetRef.current?.dismiss();
      DeviceEventEmitter.emit("empresa-troca-loading", true);

      // Precisa de internet para gerar o novo token.
      const online = await SyncService.checkInternet();
      if (!online) {
        // Sem internet: desliga o skeleton e avisa.
        DeviceEventEmitter.emit("empresa-troca-loading", false);
        setSwitchingId(null);
        setOfflineVisible(true);
        return;
      }

      try {
        const resp = await Api.autenticacao.trocarEmpresa(
          emp.hash,
          emp.id_adm_colaborador ?? 0,
        );
        const token = (resp?.done as any)?.token;
        if (!resp?.success || !token) {
          DeviceEventEmitter.emit("empresa-troca-loading", false);
          Alert.alert(
            "Não foi possível trocar",
            resp?.erros?.[0] || "Tente novamente em instantes.",
          );
          return;
        }
        const id = (resp.done as any)?.idColaborador;
        await signIn(token, id ? String(id) : undefined);
        // Atualiza o firebase token na NOVA empresa (signIn já trocou o token de API).
        void syncFirebaseToken(id);
        // Sinaliza a Home: ativa o skeleton e força o iniciar() da NOVA empresa
        // (a Home aguarda o fetch novo, então não pisca os dados da empresa anterior).
        // O loader do header CONTINUA ligado — a Home o desliga só quando terminar de
        // carregar a página da nova empresa (evento 'empresa-troca-loading' = false).
        DeviceEventEmitter.emit("empresa-trocada");
        router.replace("/(tabs)/home");
      } catch {
        DeviceEventEmitter.emit("empresa-troca-loading", false);
        Alert.alert(
          "Não foi possível trocar",
          "Verifique sua conexão e tente novamente.",
        );
      } finally {
        setSwitchingId(null);
      }
    },
    [switchingId, empresaAtualId, signIn, router],
  );

  return (
    <>
      <BottomSheetModal
        ref={sheetRef}
        enableDynamicSizing
        maxDynamicContentSize={Math.floor(windowHeight * 0.9)}
        backdropComponent={(props: BottomSheetBackdropProps) => (
          <BottomSheetBackdrop
            {...props}
            appearsOnIndex={0}
            disappearsOnIndex={-1}
            pressBehavior="close"
          />
        )}
        handleIndicatorStyle={{ backgroundColor: "#cdd9df" }}
        backgroundStyle={{
          backgroundColor: "white",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
        }}
      >
        <BottomSheetScrollView showsVerticalScrollIndicator={false}>
          {/* WEB: o contentContainerStyle do RN vira o wrapper `.conteudo`; o
              `Math.max(insets.bottom, 16) + 14` vira `max(var(--safe-bottom), 16px) + 14px`. */}
          <div className={s.conteudo}>
            <span className={s.titulo}>Trocar de empresa</span>
            <span className={s.subtitulo}>
              Selecione a empresa para alternar rapidamente
            </span>

            {loadingList ? (
              <div className={s.estadoCarregando}>
                <ActivityIndicator color={PRIMARY} />
                <span className={s.textoCarregando}>Carregando empresas…</span>
              </div>
            ) : semConexao ? (
              <div className={s.estadoSemConexao}>
                <div className={s.circuloSemConexao}>
                  <MaterialCommunityIcons
                    name="wifi-off"
                    size={26}
                    color="#e74c3c"
                  />
                </div>
                <span className={s.tituloSemConexao}>
                  Sem acesso no momento
                </span>
                <span className={s.textoSemConexao}>
                  Você está sem internet. Conecte-se para listar e trocar de
                  empresa.
                </span>
                <button
                  type="button"
                  onClick={() => void carregar()}
                  className={cx(s.btnReset, s.botaoTentarNovamente)}
                >
                  <MaterialIcons name="refresh" size={16} color={PRIMARY} />
                  <span className={s.textoTentarNovamente}>
                    Tentar novamente
                  </span>
                </button>
              </div>
            ) : empresas.length === 0 ? (
              <div className={s.estadoVazio}>
                <MaterialCommunityIcons
                  name="office-building-outline"
                  size={28}
                  color={SECONDARY}
                />
                <span className={s.textoVazio}>
                  Nenhuma empresa encontrada.
                </span>
              </div>
            ) : (
              <div className={s.lista}>
                {empresas.map((emp) => {
                  // Seleção comparada por `id` ÚNICO (igual ao Onboarding) → GARANTE no máximo
                  // UMA empresa marcada como atual, mesmo com vínculos idênticos exceto o cargo.
                  const atual = emp.id === empresaAtualId;
                  const carregando = switchingId === emp.id;
                  const ativo =
                    String(emp.ativo_inativo).toUpperCase() === "A";
                  return (
                    <button
                      type="button"
                      key={emp.id}
                      onClick={() => trocar(emp)}
                      disabled={!!switchingId}
                      className={cx(
                        s.btnReset,
                        s.item,
                        atual && s.itemAtual,
                        switchingId && !carregando && s.itemEsmaecido,
                      )}
                    >
                      <div className={s.logoBox}>
                        <Image
                          source={getEmpresaLogo(emp.logo)}
                          className={s.logo}
                          resizeMode="contain"
                          alt=""
                        />
                      </div>

                      <div className={s.info}>
                        <span className={cx(s.nome, s.umaLinha)}>
                          {emp.nome}
                        </span>
                        {emp.cargo ? (
                          <span className={cx(s.cargo, s.umaLinha)}>
                            {emp.cargo}
                          </span>
                        ) : null}
                        <div className={s.chips}>
                          {emp.id_adm_colaborador || emp.matricula ? (
                            <div className={s.chipId}>
                              <MaterialCommunityIcons
                                name="card-account-details-outline"
                                size={13}
                                color={PRIMARY}
                              />
                              <span className={s.chipIdTexto}>
                                {[
                                  emp.id_adm_colaborador
                                    ? `ID ${emp.id_adm_colaborador}`
                                    : null,
                                  emp.matricula
                                    ? `Mat. ${emp.matricula}`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join("  ·  ")}
                              </span>
                            </div>
                          ) : null}
                          {emp.ativo_inativo ? (
                            <div
                              className={cx(
                                s.chipStatus,
                                ativo ? s.chipStatusAtivo : s.chipStatusInativo,
                              )}
                            >
                              <MaterialCommunityIcons
                                name={ativo ? "check-circle" : "close-circle"}
                                size={12}
                                color={ativo ? "#1e8a3c" : "#c53030"}
                              />
                              <span
                                className={cx(
                                  s.chipStatusTexto,
                                  ativo
                                    ? s.chipStatusTextoAtivo
                                    : s.chipStatusTextoInativo,
                                )}
                              >
                                {ativo ? "Ativo" : "Inativo"}
                              </span>
                            </div>
                          ) : null}
                          {atual ? (
                            <div className={s.chipAtual}>
                              <MaterialCommunityIcons
                                name="check-circle"
                                size={12}
                                color={SUCCESS}
                              />
                              <span className={s.chipAtualTexto}>
                                Empresa atual
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {carregando ? (
                        <ActivityIndicator color={PRIMARY} />
                      ) : atual ? (
                        <div className={s.checkAtual}>
                          <MaterialIcons name="check" size={16} color="#fff" />
                        </div>
                      ) : (
                        <MaterialIcons
                          name="chevron-right"
                          size={22}
                          color="#c2d0d6"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </BottomSheetScrollView>
      </BottomSheetModal>

      {/* Modal central: troca de empresa precisa de internet */}
      <Modal
        visible={offlineVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setOfflineVisible(false)}
      >
        <div className={s.offlineFundo}>
          <div className={s.offlineCard} role="dialog" aria-modal="true">
            <div className={s.offlineCirculo}>
              <MaterialCommunityIcons
                name="wifi-off"
                size={30}
                color="#e74c3c"
              />
            </div>
            <span className={s.offlineTitulo}>Sem conexão</span>
            <span className={s.offlineTexto}>
              A troca de empresa precisa de internet para ser realizada.
              Conecte-se e tente novamente.
            </span>
            <button
              type="button"
              onClick={() => setOfflineVisible(false)}
              className={cx(s.btnReset, s.offlineBotao)}
            >
              <span className={s.offlineBotaoTexto}>Entendi</span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
});

EmpresaSwitcher.displayName = "EmpresaSwitcher";

export default EmpresaSwitcher;
